// src/frontend/js/modern/ui/dropdown-handler.js

export class DropdownHandler {
	constructor() {
		this._timeout = null;
		this._isTouchDevice = 'ontouchstart' in window || (window.DocumentTouch && document instanceof DocumentTouch);
		this._boundEvents = [];
		this._swipeTrigger = false;
		this._hideTimers = new Map();
		this._hoverIntentTimers = new Map();
		this._hoverIntentOver = new Map();
	}

	init() {
		if (this._isTouchDevice) {
			this._bindTouchEvents();
		} else {
			this._bindDesktopEvents();
		}
		this._bindScrollResize();
	}

	_bindTouchEvents() {
		const onTouchStart = (e) => {
			const wrapper = e.target.closest('.wlfmc-counter-wrapper.show-list-on-hover, .wlfmc-counter-wrapper.show-list-on-click');
			if (wrapper) {
				this._swipeTrigger = false;
			}
		};
		const onTouchMove = (e) => {
			const wrapper = e.target.closest('.wlfmc-counter-wrapper.show-list-on-hover, .wlfmc-counter-wrapper.show-list-on-click');
			if (wrapper) {
				this._swipeTrigger = true;
			}
		};
		const onTouchEnd = (e) => {
			const link = e.target.closest('.wlfmc-counter-wrapper .wlfmc-counter.has-dropdown a:not(.remove_from_wishlist)');
			if (link) {
				e.stopImmediatePropagation();
				e.preventDefault();
				window.location.href = link.getAttribute('href');
				return;
			}
			const counterWrapper = e.target.closest('.wlfmc-counter-wrapper');
			if (!counterWrapper) {
				this._hideAllDropdowns();
				return;
			}
			const trigger = e.target.closest(
				'.wlfmc-counter-wrapper.show-list-on-hover .wlfmc-counter.has-dropdown, ' +
				'.wlfmc-counter-wrapper.show-list-on-click .wlfmc-counter.has-dropdown'
			);
			if (!trigger) return;
			const wrapper = trigger.closest('.wlfmc-counter-wrapper');
			const dropdown = wrapper.querySelector('.wlfmc-counter-dropdown');
			if (!dropdown) return;
			if (wrapper.classList.contains('wlfmc-first-touch')) {
				if (!this._swipeTrigger) {
					this._hideAllDropdowns();
				}
			} else {
				e.stopImmediatePropagation();
				e.preventDefault();
				this._showDropdown(dropdown, wrapper);
				wrapper.classList.add('wlfmc-first-touch');
			}
		};
		document.addEventListener('touchstart', onTouchStart, { passive: true });
		document.addEventListener('touchmove', onTouchMove, { passive: true });
		document.addEventListener('touchend', onTouchEnd, { passive: false });
		this._boundEvents.push(
			{ target: document, event: 'touchstart', handler: onTouchStart, options: { passive: true } },
			{ target: document, event: 'touchmove', handler: onTouchMove, options: { passive: true } },
			{ target: document, event: 'touchend', handler: onTouchEnd, options: { passive: false } }
		);
	}

	_bindDesktopEvents() {
		const HOVER_INTENT_DELAY = 100;
		const onClick = (e) => {
			const trigger = e.target.closest('.wlfmc-counter-wrapper.show-list-on-click .wlfmc-counter.has-dropdown');
			if (trigger) {
				e.stopImmediatePropagation();
				e.preventDefault();
				const wrapper = trigger.closest('.wlfmc-counter-wrapper');
				const dropdown = this._getDropdown(trigger, wrapper);
				if (dropdown) {
					this._toggleDropdown(dropdown, wrapper);
				}
				return false;
			}
			if (!e.target.closest('.wlfmc-counter-dropdown') && !e.target.closest('.wlfmc-counter.has-dropdown')) {
				this._hideAllDropdowns();
			}
		};
		const onMouseOver = (e) => {
			const trigger = e.target.closest('.wlfmc-counter-wrapper.show-list-on-hover .wlfmc-counter.has-dropdown');
			if (trigger) {
				const wrapper = trigger.closest('.wlfmc-counter-wrapper');
				const dataId = wrapper.dataset.id || trigger.getAttribute('data-id') || 'default';
				// Clear pending hide timer (hoverIntent timeout)
				if (this._hideTimers.has(dataId)) {
					clearTimeout(this._hideTimers.get(dataId));
					this._hideTimers.delete(dataId);
				}
				if (!this._hoverIntentOver.has(dataId)) {
					if (this._hoverIntentTimers.has(dataId)) {
						clearTimeout(this._hoverIntentTimers.get(dataId));
					}
					const timer = setTimeout(() => {
						this._hoverIntentOver.set(dataId, true);
						const dropdown = this._getDropdown(trigger, wrapper);
						if (dropdown) this._showDropdown(dropdown, wrapper);
						this._hoverIntentTimers.delete(dataId);
					}, HOVER_INTENT_DELAY);
					this._hoverIntentTimers.set(dataId, timer);
				}
				return;
			}
			const dd = e.target.closest('.wlfmc-counter-dropdown');
			if (dd) {
				const wrapper = this._findWrapperForDropdown(dd);
				if (wrapper && wrapper.classList.contains('show-list-on-hover')) {
					const dataId = wrapper.dataset.id || 'default';
					if (this._hideTimers.has(dataId)) {
						clearTimeout(this._hideTimers.get(dataId));
						this._hideTimers.delete(dataId);
					}
				}
			}
		};
		const onMouseOut = (e) => {
			const dd = e.target.closest('.wlfmc-counter-dropdown');
			const trigger = e.target.closest('.wlfmc-counter-wrapper.show-list-on-hover .wlfmc-counter.has-dropdown');
			let wrapper = null;
			let dropdown = null;
			if (dd) {
				wrapper = this._findWrapperForDropdown(dd);
				dropdown = dd;
			} else if (trigger) {
				wrapper = trigger.closest('.wlfmc-counter-wrapper');
				dropdown = this._getDropdown(trigger, wrapper);
			}
			if (!wrapper || !dropdown || !wrapper.classList.contains('show-list-on-hover')) return;
			if (e.relatedTarget && (dd?.contains(e.relatedTarget) || wrapper.contains(e.relatedTarget))) return;
			const dataId = wrapper.dataset.id || 'default';
			this._hoverIntentOver.delete(dataId);
			this._hideDropdown(dropdown, wrapper, HOVER_INTENT_DELAY);
		};
		document.addEventListener('click', onClick);
		document.addEventListener('mouseover', onMouseOver);
		document.addEventListener('mouseout', onMouseOut);
		this._boundEvents.push(
			{ target: document, event: 'click', handler: onClick },
			{ target: document, event: 'mouseover', handler: onMouseOver },
			{ target: document, event: 'mouseout', handler: onMouseOut }
		);
	}

	/**
	 * Reposition visible dropdowns on scroll and window resize.
	 * Only currently visible (.lists-show) dropdowns are recalculated
	 * to avoid unnecessary layout reads on hidden elements.
	 */
	_bindScrollResize() {
		const onScrollResize = () => {
			clearTimeout(this._timeout);
			this._timeout = setTimeout(() => {
				document.querySelectorAll('.wlfmc-counter-dropdown.lists-show').forEach(dd => {
					this._preparePosition(dd);
				});
			}, 50);
		};
		window.addEventListener('scroll', onScrollResize, { passive: true });
		window.addEventListener('resize', onScrollResize, { passive: true });
		this._boundEvents.push(
			{ target: window, event: 'scroll', handler: onScrollResize },
			{ target: window, event: 'resize', handler: onScrollResize }
		);
	}

	_getDropdown(trigger, wrapper) {
		const innerDropdown = wrapper?.querySelector('.wlfmc-counter-dropdown');
		if (innerDropdown) {
			const dataId = wrapper.dataset.id || trigger?.getAttribute('data-id');
			if (dataId) {
				document.querySelectorAll(`.dropdown_${dataId}`).forEach(el => {
					if (el !== innerDropdown && !el.closest('.wlfmc-counter-wrapper')) {
						el.remove();
					}
				});
			}
			return innerDropdown;
		}
		const dataId = trigger?.getAttribute('data-id') || wrapper?.dataset.id;
		if (dataId) {
			return document.querySelector(`.dropdown_${dataId}`);
		}
		return null;
	}

	/**
	 * Resolve the counter wrapper that owns a given dropdown.
	 * Checks data-id first (works for body-appended fixed dropdowns),
	 * then falls back to closest wrapper ancestor (inline dropdowns).
	 */
	_findWrapperForDropdown(dd) {
		if (dd.dataset.id) {
			const wrapper = document.querySelector(`.wlfmc-counter-wrapper[data-id="${dd.dataset.id}"]`);
			if (wrapper) return wrapper;
		}
		return dd.closest('.wlfmc-counter-wrapper');
	}

	_hideAllDropdowns() {
		this._hideTimers.forEach(timer => clearTimeout(timer));
		this._hideTimers.clear();
		this._hoverIntentTimers.forEach(timer => clearTimeout(timer));
		this._hoverIntentTimers.clear();
		document.querySelectorAll('.wlfmc-counter-dropdown.lists-show').forEach(dd => {
			dd.classList.remove('lists-show');
		});
		document.querySelectorAll('.wlfmc-first-touch').forEach(el => el.classList.remove('wlfmc-first-touch'));
		document.querySelectorAll('.wlfmc-first-click').forEach(el => el.classList.remove('wlfmc-first-click'));
	}

	_toggleDropdown(dropdown, wrapper) {
		if (dropdown.classList.contains('lists-show')) {
			this._hideDropdown(dropdown, wrapper);
		} else {
			this._showDropdown(dropdown, wrapper);
		}
	}

	_showDropdown(dropdown, wrapper) {
		if (!dropdown) return;
		const id = wrapper?.dataset.id || dropdown.dataset.id || '';
		if (this._hideTimers.has(id)) {
			clearTimeout(this._hideTimers.get(id));
			this._hideTimers.delete(id);
		}
		if (this._hoverIntentTimers.has(id)) {
			clearTimeout(this._hoverIntentTimers.get(id));
			this._hoverIntentTimers.delete(id);
		}
		if (dropdown.classList.contains('lists-show')) {
			this._preparePosition(dropdown);
			return;
		}
		this._hideAllDropdowns();
		if (dropdown.dataset.positionMode === 'fixed' || wrapper?.querySelector('.position-fixed')) {
			this._appendToBody(dropdown, wrapper);
		}
		this._preparePosition(dropdown);
		dropdown.classList.add('lists-show');
	}

	_hideDropdown(dropdown, wrapper = null, delay = 0) {
		if (!dropdown) return;
		const id = wrapper?.dataset.id || dropdown.dataset.id || '';
		if (this._hideTimers.has(id)) {
			clearTimeout(this._hideTimers.get(id));
			this._hideTimers.delete(id);
		}
		if (delay > 0) {
			const timer = setTimeout(() => {
				dropdown.classList.remove('lists-show');
				document.querySelectorAll('.wlfmc-first-touch').forEach(el => el.classList.remove('wlfmc-first-touch'));
				document.querySelectorAll('.wlfmc-first-click').forEach(el => el.classList.remove('wlfmc-first-click'));
				this._hideTimers.delete(id);
			}, delay);
			this._hideTimers.set(id, timer);
		} else {
			dropdown.classList.remove('lists-show');
		}
	}

	_appendToBody(dropdown, wrapper) {
		if (dropdown.parentElement === document.body) return;
		const counterType = wrapper.classList.contains('wlfmc-lists-counter-wrapper')
			? 'wlfmc-premium-list-counter'
			: (wrapper.classList.contains('wlfmc-waitlist-counter-wrapper')
				? 'wlfmc-waitlist-counter'
				: 'wlfmc-wishlist-counter');
		const elementorWidget = wrapper.closest('.elementor-widget');
		if (elementorWidget) {
			const widgetId = elementorWidget.dataset.id || elementorWidget.id;
			const elementorId = elementorWidget.closest('[data-elementor-id]')?.dataset.elementorId || '';
			let container = document.querySelector(`.wlfmc-elementor.elementor-${elementorId}`);
			if (!container) {
				container = document.createElement('div');
				container.className = `wlfmc-elementor elementor elementor-${elementorId} ${counterType}`;
				document.body.appendChild(container);
			}
			let inner = container.querySelector(`.elementor-element-${widgetId}`);
			if (!inner) {
				inner = document.createElement('div');
				inner.className = `elementor-element elementor-element-${widgetId}`;
				container.appendChild(inner);
			}
			inner.appendChild(dropdown);
		} else {
			const widgetId = wrapper.querySelector('.wlfmc-counter-items')?.dataset.id || 'default';
			let container = document.querySelector(`.wlfmc-elementor.no-elementor-${widgetId}`);
			if (!container) {
				container = document.createElement('div');
				container.className = `wlfmc-elementor no-elementor-${widgetId} ${counterType}`;
				document.body.appendChild(container);
			}
			container.appendChild(dropdown);
		}
	}

	/**
	 * Master position dispatcher.
	 * Delegates to the appropriate strategy based on the positioning mode
	 * so that scroll/resize recalculations always use correct coordinates.
	 */
	_preparePosition(dropdown) {
		const wrapper = this._findWrapperForDropdown(dropdown);
		if (!wrapper) return;

		if (dropdown.classList.contains('position-absolute')) {
			this._positionAbsolute(dropdown, wrapper);
		} else {
			this._positionFixed(dropdown, wrapper);
		}
	}

	/**
	 * Fixed-mode positioning.
	 * The dropdown is detached to <body>, so its coordinates are
	 * viewport-relative. On every call we read the trigger's current
	 * getBoundingClientRect() so the dropdown tracks the trigger through
	 * page scroll and window resize.
	 */
	_positionFixed(dropdown, wrapper) {
		const triggerRect = wrapper.getBoundingClientRect();
		const ddWidth = dropdown.offsetWidth;
		const ddHeight = dropdown.offsetHeight;
		const winWidth = window.innerWidth;
		const winHeight = window.innerHeight;
		const margin = 10;

		let left = triggerRect.left;
		let top = triggerRect.bottom;

		// Horizontal: keep the dropdown fully inside the viewport.
		if (left + ddWidth > winWidth - margin) {
			left = Math.max(margin, winWidth - ddWidth - margin);
		}
		if (left < margin) {
			left = margin;
		}

		// Vertical: flip above the trigger when there is not enough room below.
		if (top + ddHeight > winHeight - margin && triggerRect.top - ddHeight > margin) {
			top = triggerRect.top - ddHeight;
		}

		dropdown.style.left = left + 'px';
		dropdown.style.top = top + 'px';
	}

	/**
	 * Absolute-mode positioning.
	 * The dropdown lives inside its wrapper, so the base position is relative
	 * to the wrapper and naturally scrolls with it. We first clear any
	 * previously computed inline offsets to read the CSS-defined natural
	 * position, then apply viewport-overflow clamping.
	 *
	 * This avoids the accumulation bug where getComputedStyle values from a
	 * previous run were fed back into the next calculation.
	 */
	_positionAbsolute(dropdown, wrapper) {
		// Reset inline overrides so offsetLeft/offsetTop reflect the CSS anchor.
		dropdown.style.left = '';
		dropdown.style.top = '';

		const naturalLeft = dropdown.offsetLeft;
		const naturalTop = dropdown.offsetTop;

		// Force a layout read after reset so the rect matches the natural position.
		const ddRect = dropdown.getBoundingClientRect();
		const winWidth = window.innerWidth;
		const docHeight = document.documentElement.scrollHeight;
		const margin = 50;

		let left = naturalLeft;
		let top = naturalTop;

		// Horizontal: pull back inside the right edge if overflowing.
		if (ddRect.right > winWidth - margin) {
			left -= (ddRect.right - winWidth + margin);
		}

		// If the correction pushed us past the left edge, clamp.
		if (ddRect.left + (left - naturalLeft) < margin) {
			left = naturalLeft + (margin - ddRect.left);
		}

		// Vertical: cap height if taller than the document.
		if (docHeight < ddRect.height) {
			dropdown.style.height = (docHeight - margin) + 'px';
			dropdown.style.overflowY = 'auto';
			top = 0;
		} else if (ddRect.bottom > docHeight - margin) {
			// Shift up so the dropdown fits inside the document.
			top -= (ddRect.bottom - docHeight + margin);
		}

		dropdown.style.left = left + 'px';
		dropdown.style.top = top + 'px';
	}

	destroy() {
		this._boundEvents.forEach(({ target, event, handler, options }) => {
			target.removeEventListener(event, handler, options);
		});
		this._boundEvents = [];
		this._hideTimers.forEach(timer => clearTimeout(timer));
		this._hideTimers.clear();
		this._hoverIntentTimers.forEach(timer => clearTimeout(timer));
		this._hoverIntentTimers.clear();
	}
}
